import { useTranslation } from 'react-i18next';
import {
  type ScheduleItem,
  type ScheduleHealthSummary,
  type ScheduleSettings,
  type HolidayInfo,
  formatDateLabel,
  formatDateFull,
  parseISODate,
  toISODate,
  getHealthSummaryColor,
  getHealthSummaryLabel,
  getClientHealthLabel,
  workingDayDiff,
} from '@/lib/scheduleUtils';

interface Props {
  includedItems: ScheduleItem[];
  criticalItemIds: Set<string>;
  healthSummary: ScheduleHealthSummary | null;
  viewerRole?: 'management' | 'subcontractor' | 'client';
  settings?: ScheduleSettings;
  holidays?: HolidayInfo[];
}

export default function ScheduleSummaryCards({
  includedItems,
  criticalItemIds,
  healthSummary,
  viewerRole = 'management',
  settings,
  holidays,
}: Props) {
  const { t } = useTranslation();

  const currentMaxEnd = includedItems
    .filter((i) => i.end_date)
    .map((i) => parseISODate(i.end_date!).getTime());
  const currentEndDate = currentMaxEnd.length ? toISODate(new Date(Math.max(...currentMaxEnd))) : null;

  const baselineMaxEnd = includedItems
    .filter((i) => i.baseline_end_date)
    .map((i) => parseISODate(i.baseline_end_date!).getTime());
  const baselineEndDate = baselineMaxEnd.length ? toISODate(new Date(Math.max(...baselineMaxEnd))) : null;

  let varianceDays = 0;
  if (currentEndDate && baselineEndDate && settings) {
    try {
      varianceDays = workingDayDiff(baselineEndDate, currentEndDate, settings, holidays || []);
    } catch {
      varianceDays = Math.ceil((Math.max(...currentMaxEnd) - Math.max(...baselineMaxEnd)) / (1000 * 60 * 60 * 24));
    }
  } else if (currentEndDate && baselineEndDate) {
    varianceDays = Math.ceil((Math.max(...currentMaxEnd) - Math.max(...baselineMaxEnd)) / (1000 * 60 * 60 * 24));
  }

  const isClient = viewerRole === 'client';
  const healthLabel = isClient
    ? (healthSummary ? getClientHealthLabel(healthSummary.overallHealth) : '—')
    : (healthSummary ? getHealthSummaryLabel(healthSummary.overallHealth) : '—');
  const healthColor = isClient
    ? (healthSummary ? getHealthSummaryColor(healthSummary.overallHealth).text : '#9CA3AF')
    : (healthSummary ? getHealthSummaryColor(healthSummary.overallHealth).text : '#9CA3AF');
  const healthBg = isClient
    ? (healthSummary ? getHealthSummaryColor(healthSummary.overallHealth).bg : '#F9FAFB')
    : (healthSummary ? getHealthSummaryColor(healthSummary.overallHealth).bg : '#F9FAFB');

  const cards = [
    {
      label: t('summaryCards.projectCompletion'),
      value: currentEndDate ? formatDateLabel(currentEndDate) : '—',
      sub: currentEndDate ? formatDateFull(currentEndDate).split(' ').slice(1).join(' ') : '',
      color: '#1A1A1A', bg: '#F9FAFB',
      show: true,
      priority: 1,
    },
    {
      label: t('summaryCards.baselineCompletion'),
      value: baselineEndDate ? formatDateLabel(baselineEndDate) : t('summaryCards.noBaseline'),
      sub: baselineEndDate ? formatDateFull(baselineEndDate).split(' ').slice(1).join(' ') : '',
      color: '#7C3AED', bg: '#F5F3FF',
      show: true,
      priority: 5,
    },
    {
      label: t('summaryCards.scheduleVariance'),
      value: baselineEndDate
        ? (varianceDays > 0 ? t('summaryCards.behindDays', { n: varianceDays }) : varianceDays < 0 ? t('summaryCards.aheadDays', { n: Math.abs(varianceDays) }) : t('summaryCards.onBaseline'))
        : '—',
      sub: '',
      color: varianceDays > 0 ? '#EF4444' : varianceDays < 0 ? '#059669' : '#1A1A1A',
      bg: varianceDays > 0 ? '#FEF2F2' : varianceDays < 0 ? '#F0FDF4' : '#F9FAFB',
      show: true,
      priority: 2,
    },
    {
      label: t('summaryCards.criticalItems'),
      value: criticalItemIds.size,
      sub: t('summaryCards.onCriticalPath'),
      color: '#EF4444', bg: '#FEF2F2',
      show: viewerRole !== 'client',
      priority: 3,
    },
    {
      label: t('summaryCards.delayedItems'),
      value: healthSummary?.delayedItems ?? 0,
      sub: t('summaryCards.behindSchedule'),
      color: '#EF4444', bg: '#FEF2F2',
      show: true,
      priority: 1,
    },
    {
      label: t('summaryCards.atRiskItems'),
      value: healthSummary?.atRiskItems ?? 0,
      sub: t('summaryCards.dueWithin3Days'),
      color: '#F59E0B', bg: '#FEF0E7',
      show: true,
      priority: 1,
    },
    {
      label: isClient ? t('summaryCards.scheduleStatus') : t('summaryCards.scheduleHealth'),
      value: healthLabel,
      sub: '',
      color: healthColor,
      bg: healthBg,
      show: true,
      priority: 1,
    },
  ];

  const visibleCards = cards.filter((c) => c.show);
  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 ${visibleCards.length >= 7 ? 'lg:grid-cols-7' : visibleCards.length >= 6 ? 'lg:grid-cols-6' : visibleCards.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
      {visibleCards.map((s) => (
        <div key={s.label} className="rounded-xl md:rounded-2xl p-2 md:p-3 text-center" style={{ backgroundColor: s.bg }}>
          <div className="text-sm font-bold" style={{ color: s.color }}>{s.value}</div>
          <div className="text-[9px] md:text-[10px] font-medium mt-0.5" style={{ color: s.color }}>{s.label}</div>
          {s.sub && <div className="text-[8px] md:text-[9px] text-[#9CA3AF] mt-0.5">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}
